import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type AbilitiesService from 'ember-can/services/abilities';
import type Location from 'garaje/models/location';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type LocationsService from 'garaje/services/locations';
import type MetricsService from 'garaje/services/metrics';
import type RoombaGraphqlService from 'garaje/services/roomba-graphql';
import type StateService from 'garaje/services/state';
import type UserFeatureFlagsService from 'garaje/services/user-feature-flags';
import { AnalyticsEventNames } from 'garaje/utils/enums';
import type { GetCompanyQuery } from 'garaje/utils/graphql/graphql-types';

import type AnalyticsRoomsController from './controller';

export interface AnalyticsRoomsRouteParams {
  location_id: string;
}

export default class AnalyticsRoomsRoute extends Route {
  @service declare router: RouterService;
  @service declare roombaGraphql: RoombaGraphqlService;
  @service declare state: StateService;
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare userFeatureFlags: UserFeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare locations: LocationsService;

  queryParams = {
    location_id: { refreshModel: true },
  };

  async model(): Promise<{ location: Location | null; company: GetCompanyQuery['company'] }> {
    return { location: this.state.currentLocation, company: await this.roombaGraphql.getCompany() };
  }

  afterModel(): void {
    this.#logMetrics();
  }

  setupController(controller: AnalyticsRoomsController, model: void, transition: Transition): void {
    super.setupController(controller, model, transition);

    const { location_id } = <AnalyticsRoomsRouteParams>this.paramsFor(this.routeName);
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const protectedController = this.controllerFor('protected');

    if (location_id && this.state.currentLocation.id != location_id) {
      protectedController.send('switchLocation', location_id, 'analytics.rooms.index');

      this.locations.loadAllTask
        .perform()
        .then(() => {
          const matchingLocation = this.locations.allActive.filter((entry) => entry.id === location_id);
          if (matchingLocation.length === 0) {
            this.router.transitionTo('unauthorized');
          }
        })
        .catch((error) => {
          throw error;
        });
    }

    if (
      this.featureFlags.isEnabled('sigma-embedded-analytics') ||
      this.userFeatureFlags.isEnabled('sigma-embedded-analytics-by-user')
    ) {
      void controller.fetchSigmaUrlTask.perform();
    }
  }

  redirect(): void {
    if (this.abilities.cannot('visit rooms analytic')) {
      void this.router.transitionTo('unauthorized');
    }
  }

  #logMetrics(): void {
    const {
      currentCompany: { id: companyId },
      currentLocation: { id: locationId },
    } = this.state;
    const details = {
      companyId,
      locationId,
      app: this.state.workplaceSubscription?.app,
      plan: this.state?.workplaceSubscription?.plan,
      userId: this.currentAdmin?.id || '',
      employeeId: this.currentAdmin.employee?.id || '',
    };
    this.metrics.trackEvent(AnalyticsEventNames.ROOMS_DASHBOARD_VISITED, details);
    this.metrics.logMonitorEvent(AnalyticsEventNames.ROOMS_DASHBOARD_VISITED, details);
  }
}
